import React, { useCallback, useState } from 'react';
import {
  FormControl,
  FormLabel,
  Input,
  Box,
  List,
  ListItem,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { useGooglePlacesAutocomplete } from 'hooks/useGooglePlacesAutocomplete';
import { parseGooglePlaceAddress } from 'utils/GooglePlaceAddress';

// Full-address search box: fetches Places suggestions as the user types and,
// on selection, hands the caller a parsed { line1, line2, city, state,
// postalCode, country } object. Deliberately has no knowledge of the form it
// feeds (react-hook-form, localized-regions, ...) so it can be reused on any
// surface that needs a "search full address" input.
const GoogleAddressAutocomplete = ({
  labelText = 'Search full address',
  placeholder = 'Start typing a street, city or zip...',
  textColor,
  onAddressSelected,
}) => {
  const { isLoaded, getPlacePredictions, getPlaceDetails } =
    useGooglePlacesAutocomplete();

  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);

  const handleInputChange = useCallback(
    (e) => {
      const value = e.target.value;
      setInputValue(value);

      if (!value) {
        setSuggestions([]);
        return;
      }

      getPlacePredictions({ input: value }, (predictions, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          predictions
        ) {
          setSuggestions(predictions);
        } else {
          setSuggestions([]);
        }
      });
    },
    [getPlacePredictions],
  );

  const handleSuggestionClick = useCallback(
    (prediction) => {
      getPlaceDetails(
        {
          placeId: prediction.place_id,
          fields: ['address_components', 'formatted_address', 'name', 'types'],
        },
        (place, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            place
          ) {
            onAddressSelected(parseGooglePlaceAddress(place));
          }
          setInputValue('');
          setSuggestions([]);
        },
      );
    },
    [getPlaceDetails, onAddressSelected],
  );

  return (
    <FormControl position="relative">
      <FormLabel fontSize="sm" color={textColor} fontWeight="medium">
        {labelText}
      </FormLabel>
      <Input
        value={inputValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        isDisabled={!isLoaded}
        color={textColor}
        borderColor="gray.300"
        _focus={{
          borderColor: 'reviveOrange',
          boxShadow: '0 0 0 1px reviveOrange',
        }}
        _hover={{ borderColor: 'gray.400' }}
      />

      {suggestions.length > 0 && (
        <Box
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          bg="white"
          mt={1}
          maxH="200px"
          overflowY="auto"
          position="absolute"
          width="100%"
          zIndex={10}
        >
          <List>
            {suggestions.map((item) => (
              <ListItem
                key={item.place_id}
                p={2}
                _hover={{ background: 'gray.100', cursor: 'pointer' }}
                onClick={() => handleSuggestionClick(item)}
              >
                {item.description}
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </FormControl>
  );
};

GoogleAddressAutocomplete.propTypes = {
  labelText: PropTypes.string,
  placeholder: PropTypes.string,
  textColor: PropTypes.string,
  onAddressSelected: PropTypes.func.isRequired,
};

export default GoogleAddressAutocomplete;
